import LanguageSwitcher from '_components/LanguageSwitcher';
import Socials from '_components/Socials';
import cn from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import {
  useI18next,
  useTranslation,
} from 'gatsby-plugin-react-i18next';
import React from 'react';
import Section from '../../containers/Section';
import FooterQRCode from '../FooterQRCode';
import Link from '../Link';
import LogoWordmark from '../NewNavigation/assets/logo-wordmark.min.svg';
import { FooterVariant } from '../NewNavigation/models';
import { PAGE_SPECIFIC_STYLES } from './constants';
import EmbeddedCta from '_components/Blog/EmbeddedCta';

interface FooterProps {
  locale: string;
  variant?: string;
  page?: string;
  footerRef?: React.RefObject<HTMLDivElement>;
  localesAvailable?: { language: string; url: string; '@type': string }[];
}

const Footer: React.FC<FooterProps> = props => {
  const { t } = useTranslation();
  const {
    locale = 'en-US',
    variant = 'light',
    page = 'default',
    footerRef,
    localesAvailable,
  } = props;

  const { language } = useI18next();

  const footerData = useStaticQuery(
    graphql`
      query {
        contact: allContentfulFooterNavigation(
          filter: { title: { eq: "Contact" } }
        ) {
          nodes {
            id
            title
            displayTitle
            node_locale
            footerLink {
              id
              link
              text
              fieldId
              node_locale
              nofollow
            }
          }
        }
        footerMenu: allContentfulFooterNavigation(
          filter: {
            contentful_id: {
              in: [
                "7bcW4hIUpjoKWUYRMISBhJ"
                "3GXfcfBHVbVsmXWLapdeGH"
                "6o7JaJlqGjWpJXZL2we6Xe"
              ]
            }
          }
          sort: { order: ASC }
        ) {
          nodes {
            id
            title
            order
            displayTitle
            node_locale
            footerLink {
              id
              link
              text
              node_locale
              nofollow
            }
          }
        }
        qrCodes: allContentfulFooterQrCode {
          nodes {
            id
            title
            node_locale
            qrCode {
              url
            }
          }
        }
      }
    `
  );

  const filterFooterMenu = data =>
    data.nodes.filter(item => item.node_locale.includes(locale));

  return (
    <div
      ref={footerRef}
      id="footer"
      className={cn(
        'relative text-gray-3-dark pt-4 md:pt-24 lg:pt-32 ',
        page && PAGE_SPECIFIC_STYLES[page]
          ? PAGE_SPECIFIC_STYLES[page].wrapper
          : ''
      )}
    >
      <Section>
        <div className="text-base font-medium mb-20 md:mb-24 lg:mb-32 flex flex-wrap">
          <div className="w-full lg:w-1/4 space-y-4 my-10 md:mt-0 lg:mb-0 pt-6">
            <Link to="/" className="block">
              <LogoWordmark />
            </Link>
            <ul className="relative">
              {filterFooterMenu(footerData.contact)[0].footerLink.map(
                (
                  item: {
                    id: React.Key;
                    link: string;
                    text: string;
                    fieldId: string;
                  },
                  index: number
                ) => {

                    return (
                      <li key={item.id} className="mt-4">
                        <Link
                          to={item.link}
                          className={cn(
                            'hover:opacity-80 active:opacity-60 lg:max-w-[225px] block transition',
                            index === 0 && 'text-indigo-1'
                          )}
                          rel="nofollow"
                        >
                          {item.text}
                        </Link>
                      </li>
                    );
                }
              )}
            </ul>
            <div className="flex items-center space-x-4">
              {footerData?.qrCodes?.nodes
                ?.filter(i => i.node_locale.includes(locale))
                ?.map(qrCode => {
                  return (
                    <FooterQRCode
                      key={qrCode.id}
                      title={qrCode?.title}
                      imageUrl={qrCode?.qrCode?.url}
                    />
                  );
                })}
            </div>
          </div>
          {filterFooterMenu(footerData.footerMenu).map((section, i) => {
            return (
              <div
                key={section.id}
                className="w-1/2 sm:w-1/3 lg:w-1/4 space-y-4 p-6"
              >
                <p
                  className={cn(
                    'font-semibold text-base border-b-2 border-gray-5-light pb-1',
                    {
                      'text-white': variant === FooterVariant.dark,
                    }
                  )}
                >
                  {section?.displayTitle}
                </p>
                <ul className={cn('space-y-4')}>
                  {section?.footerLink?.map(footerLinkItem => {
                    return (
                      <li key={footerLinkItem.id}>
                        <Link
                          to={footerLinkItem.link}
                          className={
                            'hover:opacity-80 active:opacity-60 transition'
                          }
                          rel={
                            footerLinkItem.nofollow != false &&
                            footerLinkItem.nofollow != null
                              ? 'nofollow'
                              : ''
                          }
                        >
                          {footerLinkItem.text}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
        <div className="border-t-2 border-gray-5-light text-base text-gray-3-dark font-medium py-8 flex flex-col lg:flex-row lg:justify-between lg:items-center">
          {page !== '404' && (
            <div className="language-switcher flex items-center">
              <LanguageSwitcher localesAvailable={localesAvailable} />
            </div>
          )}

          <p className="my-3 lg:my-0">
            <span className="whitespace-nowrap mr-8 leading-7 block sm:inline-block">
              &copy; {new Date().getFullYear()} Perpetua.
              <span className="ml-2">{t('All rights reserved.')}</span>
              {locale === 'zh-CN' ? (
                <span className="hover:opacity-80 transition">
                  <a
                    href="https://beian.miit.gov.cn/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    沪ICP备19018360号-2
                  </a>
                </span>
              ) : null}
            </span>

            <Link
              className="mr-2 sm:mr-8 whitespace-nowrap hover:opacity-80 transition"
              to={t('/privacy/')}
            >
              {t('Privacy Policy')}
            </Link>

            {locale !== 'zh-CN' && (
              <Link
                className="mr-2 sm:mr-8 whitespace-nowrap hover:opacity-80 transition"
                to={''}
                onClick={() => window?.Optanon?.ToggleInfoDisplay()}
              >
                {t('Cookies')}
              </Link>
            )}

            <Link
              className="mr-2 sm:mr-8 whitespace-nowrap hover:opacity-80 transition"
              to={t('/tos/')}
            >
              {t('Terms of Service')}
            </Link>
            {locale === 'de' && (
              <Link
                className="mr-2 sm:mr-8 whitespace-nowrap hover:opacity-80 transition"
                to="/de/impressum/"
              >
                Impressum
              </Link>
            )}
          </p>
          <Socials />
        </div>
      </Section>
    </div>
  );
};

export default React.memo(Footer);
